import {
  CircleChevronLeftFill,
  CircleChevronRightFill,
} from "@gravity-ui/icons";
import cn from "classnames";
import { PropsWithChildren, useState } from "react";

import { ITEM_WIDTH, GAP } from "./ImageSlider.constants";
import css from "./ImageSlider.module.scss";

interface SliderProps {
  images: string[];
  size?: number;
}

export const ImageSlider = ({
  images,
  size = 400,
}: PropsWithChildren<SliderProps>) => {
  const [slide, setSlide] = useState(0);
  const [offset, setOffset] = useState(0);

  const visibleCount = Math.min(3, images.length);
  const panelWidth = visibleCount * ITEM_WIDTH - GAP;

  const updateOffset = (index: number) => {
    const minVisible = offset / ITEM_WIDTH;
    const maxVisible = minVisible + visibleCount - 1;

    if (index < minVisible) {
      setOffset(index * ITEM_WIDTH);
    } else if (index > maxVisible) {
      setOffset((index - visibleCount + 1) * ITEM_WIDTH);
    }
  };

  const prevSlide = () => {
    setSlide((v) => {
      const next = Math.max(v - 1, 0);
      updateOffset(next);
      return next;
    });
  };

  const nextSlide = () => {
    setSlide((v) => {
      const next = Math.min(v + 1, images.length - 1);
      updateOffset(next);
      return next;
    });
  };

  const selectedImage = images[slide];

  return (
    <div
      className={css.slider}
      style={
        {
          "--slider-size": `${size}px`,
        } as React.CSSProperties
      }
    >
      <div>
        <div className={cn(css.activeSlide, css.slide)}>
          <img
            src={selectedImage}
            alt=""
            className={css.image}
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      </div>

      <div className={css.controls}>
        <CircleChevronLeftFill className={css.arrow} onClick={prevSlide} />
        <div className={css.scrollPanel} style={{ width: panelWidth }}>
          <div
            className={css.miniSlider}
            style={{ transform: `translateX(${-offset}px)` }}
          >
            {images.map((image, index) => (
              <div
                className={css.miniSlide}
                style={{
                  border:
                    index === slide ? "var(--active-slide-border)" : "none",
                }}
                onClick={() => {
                  setSlide(index);
                  updateOffset(index);
                }}
                key={image}
              >
                <img
                  loading={"lazy"}
                  src={image}
                  alt=""
                  className={css.image}
                  style={{ width: "100%", height: "100%" }}
                />
              </div>
            ))}
          </div>
        </div>
        <CircleChevronRightFill className={css.arrow} onClick={nextSlide} />
      </div>
    </div>
  );
};
